import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useState,
} from 'react'
import type { AuthRecord } from './authContext'

export type CurrentAuth = (() => AuthRecord | null | undefined) & {
  waitForUpdate?: () => Promise<void>
}

const AuthSessionContext = createContext<CurrentAuth | undefined>(undefined)

type AuthSnapshot = {
  current: CurrentAuth
  publish: (auth: AuthRecord | null) => void
}

function createAuthSnapshot(): AuthSnapshot {
  let committed: AuthRecord | null = null
  const listeners = new Set<() => void>()
  return {
    current: Object.assign(() => committed, {
      waitForUpdate: () =>
        new Promise<void>((resolve) => listeners.add(resolve)),
    }),
    publish: (auth) => {
      committed = auth
      listeners.forEach((notify) => notify())
      listeners.clear()
    },
  }
}

function useCommitAuthSnapshot(snapshot: AuthSnapshot, auth: AuthRecord) {
  useLayoutEffect(() => snapshot.publish(auth), [snapshot, auth])
  useLayoutEffect(() => () => snapshot.publish(null), [snapshot])
}

function AuthSessionCommit({
  snapshot,
  auth,
}: {
  snapshot: AuthSnapshot
  auth: AuthRecord
}): null {
  useCommitAuthSnapshot(snapshot, auth)
  return null
}

// A confirmation or shared request can outlive the row that started it. Only
// the session owner decides whether its authorization snapshot is still live.
export function AuthSessionProvider({
  auth,
  children,
}: {
  auth: AuthRecord
  children: React.ReactNode
}): JSX.Element {
  const [snapshot] = useState(createAuthSnapshot)
  return (
    <AuthSessionContext.Provider value={snapshot.current}>
      {/* Publish before descendant layout effects (including SWR) run. A
          layout effect on this provider itself would run after its children. */}
      <AuthSessionCommit snapshot={snapshot} auth={auth} />
      {children}
    </AuthSessionContext.Provider>
  )
}

export function useCurrentAuth(auth: AuthRecord): CurrentAuth {
  const session = useContext(AuthSessionContext)
  // Standalone consumers without AuthProvider retain a local lifetime. The app
  // always supplies the shared session; isolated component tests may not.
  const [local] = useState(createAuthSnapshot)
  useCommitAuthSnapshot(local, auth)
  return session ?? local.current
}
